var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import CSelect from './select';
import Button from './button';
const supportedVideoMimeType = () => {
    const candidates = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
        'video/mp4',
    ];
    return candidates.find((x) => window.MediaRecorder && MediaRecorder.isTypeSupported(x)) || '';
};
const environmentCamera = (cameras) => cameras.find((x) => /environment|back|rear/i.test(x.label));
export default (props) => {
    const liveVideoRef = React.useRef(null);
    const mountedRef = React.useRef(true);
    const previewRequestRef = React.useRef(0);
    const streamRef = React.useRef(null);
    const recorderRef = React.useRef(null);
    const chunksRef = React.useRef([]);
    const startedAtRef = React.useRef(null);
    const [status, setStatus] = React.useState('idle');
    const [error, setError] = React.useState(null);
    const [stream, setStream] = React.useState(null);
    const [videoDevices, setVideoDevices] = React.useState([]);
    const [audioDevices, setAudioDevices] = React.useState([]);
    const [selectedVideoId, setSelectedVideoId] = React.useState(null);
    const [selectedAudioId, setSelectedAudioId] = React.useState(null);
    const [capture, setCapture] = React.useState(null);
    const stopMediaStream = React.useCallback((current) => {
        current === null || current === void 0 ? void 0 : current.getTracks().forEach((track) => track.stop());
    }, []);
    const stopStream = React.useCallback(() => {
        stopMediaStream(streamRef.current);
        streamRef.current = null;
        if (liveVideoRef.current) {
            liveVideoRef.current.srcObject = null;
        }
        setStream((current) => {
            stopMediaStream(current);
            return null;
        });
    }, [stopMediaStream]);
    const clearCapture = React.useCallback(() => {
        setCapture((current) => {
            if (current === null || current === void 0 ? void 0 : current.url) {
                URL.revokeObjectURL(current.url);
            }
            return null;
        });
    }, []);
    const refreshDevices = React.useCallback((currentStream) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!((_a = navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.enumerateDevices)) {
            setVideoDevices([]);
            setAudioDevices([]);
            return;
        }
        const devices = yield navigator.mediaDevices.enumerateDevices();
        if (!mountedRef.current) {
            return;
        }
        const cameras = devices.filter((x) => x.kind === 'videoinput');
        const microphones = devices.filter((x) => x.kind === 'audioinput');
        setVideoDevices(cameras);
        setAudioDevices(microphones);
        setSelectedVideoId((current) => { var _a, _b, _c; return current || ((_a = currentStream === null || currentStream === void 0 ? void 0 : currentStream.getVideoTracks()[0]) === null || _a === void 0 ? void 0 : _a.getSettings().deviceId) || ((_b = environmentCamera(cameras)) === null || _b === void 0 ? void 0 : _b.deviceId) || ((_c = cameras[0]) === null || _c === void 0 ? void 0 : _c.deviceId) || null; });
        setSelectedAudioId((current) => { var _a; return current || ((_a = microphones[0]) === null || _a === void 0 ? void 0 : _a.deviceId) || null; });
    }), []);
    const startPreview = React.useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const previewRequest = previewRequestRef.current + 1;
        previewRequestRef.current = previewRequest;
        try {
            setStatus('checking');
            setError(null);
            stopStream();
            if (!((_a = navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.getUserMedia)) {
                setStatus('error');
                setError('This browser does not support camera capture.');
                return;
            }
            if (props.mode === 'video' && !window.MediaRecorder) {
                setStatus('error');
                setError('This browser does not support video recording.');
                return;
            }
            const nextStream = yield navigator.mediaDevices.getUserMedia({
                'video': selectedVideoId ? { 'deviceId': { 'exact': selectedVideoId } } : { 'facingMode': { 'ideal': 'environment' } },
                'audio': props.mode === 'video' && props.includeAudio ? (selectedAudioId ? { 'deviceId': { 'exact': selectedAudioId } } : true) : false,
            });
            if (!mountedRef.current || previewRequest !== previewRequestRef.current) {
                stopMediaStream(nextStream);
                return;
            }
            streamRef.current = nextStream;
            setStream(nextStream);
            setStatus('ready');
            yield refreshDevices(nextStream);
        }
        catch (e) {
            if (!mountedRef.current || previewRequest !== previewRequestRef.current) {
                return;
            }
            setStatus('error');
            setError((e === null || e === void 0 ? void 0 : e.message) || 'Unable to access camera or microphone.');
        }
    }), [props.includeAudio, props.mode, refreshDevices, selectedAudioId, selectedVideoId, stopMediaStream, stopStream]);
    React.useEffect(() => {
        mountedRef.current = true;
        return () => {
            var _a;
            mountedRef.current = false;
            previewRequestRef.current += 1;
            if (((_a = recorderRef.current) === null || _a === void 0 ? void 0 : _a.state) === 'recording') {
                recorderRef.current.stop();
            }
            stopStream();
            clearCapture();
        };
    }, [clearCapture, stopStream]);
    React.useEffect(() => {
        if (liveVideoRef.current) {
            liveVideoRef.current.srcObject = stream;
        }
    }, [stream]);
    React.useEffect(() => {
        clearCapture();
        if (status === 'ready' || status === 'recording' || status === 'captured') {
            startPreview();
        }
    }, [props.mode]);
    React.useEffect(() => {
        if (status === 'ready' || status === 'recording') {
            startPreview();
        }
    }, [props.includeAudio, selectedAudioId, selectedVideoId]);
    const setNextCapture = (nextCapture) => {
        var _a;
        clearCapture();
        setCapture(nextCapture);
        setStatus('captured');
        (_a = props.onCaptured) === null || _a === void 0 ? void 0 : _a.call(props, nextCapture);
    };
    const capturePhoto = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!liveVideoRef.current || !stream) {
            startPreview();
            return;
        }
        const video = liveVideoRef.current;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            setStatus('error');
            setError('Unable to capture photo.');
            return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const blob = yield new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
        if (!blob) {
            setStatus('error');
            setError('Unable to capture photo.');
            return;
        }
        setNextCapture({
            'kind': 'photo',
            blob,
            'url': URL.createObjectURL(blob),
            'mimeType': blob.type,
            'durationMs': null,
        });
    });
    const startRecording = () => {
        if (!stream) {
            startPreview();
            return;
        }
        const mimeType = supportedVideoMimeType();
        chunksRef.current = [];
        startedAtRef.current = Date.now();
        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        recorderRef.current = recorder;
        recorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                chunksRef.current.push(event.data);
            }
        };
        recorder.onstop = () => {
            const durationMs = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
            const blob = new Blob(chunksRef.current, { 'type': recorder.mimeType || mimeType || 'video/webm' });
            setNextCapture({
                'kind': 'video',
                blob,
                'url': URL.createObjectURL(blob),
                'mimeType': blob.type,
                durationMs,
            });
        };
        recorder.start();
        setStatus('recording');
    };
    const stopRecording = () => {
        var _a;
        if (((_a = recorderRef.current) === null || _a === void 0 ? void 0 : _a.state) === 'recording') {
            recorderRef.current.stop();
        }
    };
    const discardCapture = () => {
        clearCapture();
        setStatus(stream ? 'ready' : 'idle');
    };
    const deviceTitle = (device, fallback) => device.label || fallback;
    return _jsxs("div", { children: [_jsxs("div", { className: "row g-2 mb-3", children: [_jsx("div", { className: props.mode === 'video' ? 'col-12 col-md-4' : 'col-12 col-md-6', children: _jsx(CSelect, { small: true, inline: true, value: selectedVideoId, onChange: setSelectedVideoId, items: videoDevices, valuef: (x) => x.deviceId, titlef: (x) => deviceTitle(x, `Camera ${videoDevices.indexOf(x) + 1}`), emptyTitle: "Default Camera", disabled: status === 'recording', children: "Camera" }) }), _jsx("div", { className: props.mode === 'video' ? 'col-12 col-md-4' : 'd-none', children: _jsx(CSelect, { small: true, inline: true, value: selectedAudioId, onChange: setSelectedAudioId, items: audioDevices, valuef: (x) => x.deviceId, titlef: (x) => deviceTitle(x, `Microphone ${audioDevices.indexOf(x) + 1}`), emptyTitle: "Default Microphone", disabled: !props.includeAudio || status === 'recording', children: "Microphone" }) }), _jsxs("div", { className: props.mode === 'video' ? 'd-flex gap-2 col-12 col-md-4' : 'd-flex gap-2 col-12 col-md-6', children: [status !== 'recording' && _jsx(Button, { color: "secondary", onClick: startPreview, children: "Start Preview" }), props.mode === 'photo' && (status === 'ready' || status === 'captured') && _jsx(Button, { color: "primary", onClick: capturePhoto, children: "Take Photo" }), props.mode === 'video' && (status === 'ready' || status === 'captured') && _jsx(Button, { color: "danger", onClick: startRecording, children: "Record" }), status === 'recording' && _jsx(Button, { color: "primary", onClick: stopRecording, children: "Stop" }), !!capture && _jsx(Button, { color: "secondary", onClick: discardCapture, children: "Discard" })] })] }), !!error && _jsx("div", { className: "alert alert-danger py-2", children: error }), status === 'idle' && _jsxs("p", { className: "text-muted small", children: ["Start preview to allow camera", props.mode === 'video' && props.includeAudio ? ' and microphone' : '', " detection."] }), status === 'checking' && _jsxs("p", { className: "text-muted small", children: ["Checking camera", props.mode === 'video' && props.includeAudio ? ' and microphone' : '', " access..."] }), status === 'recording' && _jsx("p", { className: "text-danger small", children: "Recording..." }), status === 'captured' && (capture === null || capture === void 0 ? void 0 : capture.kind) === 'photo' && _jsx("p", { className: "text-muted small", children: "Photo preview ready." }), status === 'captured' && (capture === null || capture === void 0 ? void 0 : capture.kind) === 'video' && _jsxs("p", { className: "text-muted small", children: ["Video preview ready. Duration: ", Math.max(1, Math.round((capture.durationMs || 0) / 1000)), " seconds."] }), _jsx("div", { className: "bg-dark rounded overflow-hidden mb-3", style: { 'aspectRatio': '16 / 9' }, children: (capture === null || capture === void 0 ? void 0 : capture.kind) === 'photo'
                    ? _jsx("img", { src: capture.url, className: "w-100 h-100", style: { 'objectFit': 'contain' }, alt: "Captured inspection media" })
                    : (capture === null || capture === void 0 ? void 0 : capture.kind) === 'video'
                        ? _jsx("video", { src: capture.url, controls: true, className: "w-100 h-100", style: { 'objectFit': 'contain' } })
                        : _jsx("video", { ref: liveVideoRef, autoPlay: true, muted: true, playsInline: true, className: "w-100 h-100", style: { 'objectFit': 'contain' } }) })] });
};
